"use strict";
/**
 * Kalshi adapter — a thin shim over the existing Kalshi modules.
 *
 * Deliberately delegates rather than reimplements: phase 2 is a refactor whose
 * proof is that the existing suite passes unmodified, and rewriting Kalshi's
 * data path would forfeit that.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.kalshiAdapter = void 0;
exports.kalshiTotalFilled = kalshiTotalFilled;
const api_1 = require("../api");
const kalshi_fetcher_1 = require("../markets/kalshi-fetcher");
const cache_1 = require("../markets/cache");
const converters_1 = require("../markets/converters");
const orderbook_1 = require("../orderbook");
const creation_1 = require("../orders/creation");
/**
 * Contracts filled across the whole batch — the glow's rule.
 *
 * `total_filled` covers every leg; older responses carried only the two
 * components, so fall back to summing them. A zero here is an IOC that missed,
 * NOT a failure (GOTCHAS §4).
 */
function kalshiTotalFilled(response) {
    return (response.total_filled ??
        (response.liquidated_qty ?? 0) + (response.purchased_qty ?? 0));
}
exports.kalshiAdapter = {
    id: "kalshi",
    label: "Kalshi",
    orderErrorLabel: "Order",
    capabilities: {
        leagueSelector: "series-prefix-suffix",
        // A binary contract names only the YES side; NO falls back to
        // "Opposing Team" (ui/market-display.ts).
        sideLabels: "yes-only",
        sellFullPosition: true,
        fanOutTarget: true,
        sportModes: true,
        fractionalQuantity: true,
        priceTicks: "uniform",
        orderbookIntervalMs: 250,
        pnlDetail: true,
        marketOrder: "id-length",
    },
    fetchEvents(query) {
        return (0, kalshi_fetcher_1.fetchKalshiEvents)(query.category);
    },
    fetchEventMarkets(eventId) {
        return (0, kalshi_fetcher_1.fetchKalshiEventMarkets)(eventId);
    },
    cachedEvents() {
        // Kalshi events are sorted soonest-first; the other venues arrive sorted.
        return (0, converters_1.sortDisplayEventsByDate)((0, cache_1.getKalshiEventCache)().map(converters_1.kalshiEventToDisplayEvent));
    },
    startOrderbook(market, opts) {
        return (0, orderbook_1.startKalshiOrderbookPolling)(market.id, opts.intervalMs ?? this.capabilities.orderbookIntervalMs, opts.onUpdate, opts.onError);
    },
    buildOrderRequest(ctx) {
        return (0, creation_1.createOrderRequest)(ctx.marketId, ctx.side, ctx.action, ctx.options);
    },
    async placeOrder(request) {
        const response = await (0, api_1.placeOrder)(request);
        const order = response.orders?.[0] ?? null;
        const totalFilled = kalshiTotalFilled(response);
        return {
            // Judged on TOTAL fills across the batch, not the first order: a
            // zero-fill IOC must read as a miss so the operator re-fires.
            succeeded: Boolean(order?.order_id) && totalFilled > 0,
            filledQty: totalFilled,
            statusForState: response,
            order: order,
            raw: response,
            ...(response.warning ? { warning: response.warning } : {}),
        };
    },
};
